import { Modal, ModalProps } from 'react-bootstrap';
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { ControlledModal } from '../../common/ControlledModal';
import { uploadFileSchema, UploadFileSchema } from '../Forms/schemas';
import { UploadFileForm } from '../Forms/UploadFileForm';
import { useFileExplorerContext } from '../../../hooks/useFileExplorerContext';
import { useFileExplorerMutation } from '../../../hooks/useFileExplorerMutation';
import { TreeFile, uploadFiles } from '../services';
import { folderKeys } from '../utils/queryKeys';

const GENERIC_ERROR_MESSAGE = 'Ocurrió un error al subir el archivo. Por favor, intenta nuevamente.';

export function UploadFileModal({
  show, onHide, files,
}: ModalProps & { files?: File[] }) {
  const { activeFolder, addNewlyAddedItems } = useFileExplorerContext();
  const mutationFn = (data: UploadFileSchema) => uploadFiles(data);
  const form = useForm<UploadFileSchema>({
    resolver: zodResolver(uploadFileSchema),
    values: {
      files: files ?? [],
      filesMeta: files ? files.map(() => ({ title: undefined, description: undefined })) : [],
      category: activeFolder?.id ?? 0,
      duplicateAction: 'backup',
    },
  });

  const handleSuccess = () => {
    form.reset();
    onHide?.();
  };

  const {
    mutateAsync, isLoading, reset,
  } = useFileExplorerMutation({
    mutationFn,
    onSuccess: handleSuccess,
  });

  const queryClient = useQueryClient();

  if (!activeFolder) return null;

  const handleUploadWithToast = (data: UploadFileSchema) => {
    const uploadPromise = mutateAsync(data, {
      onSuccess(response) {
        const newFiles = response as TreeFile[];
        addNewlyAddedItems(newFiles.map((file) => file.id));
      },
      async onError() {
        await queryClient.invalidateQueries({ queryKey: folderKeys.detail(activeFolder.id) });
      },
    });
    const isSingleFile = data.files.length === 1;
    toast.promise(uploadPromise, {
      loading: `Estamos subiendo tus archivo${isSingleFile ? '' : 's'}...`,
      success: () => {
        const fileCount = data.files.length;
        return isSingleFile
          ? 'Archivo subido exitosamente'
          : `${fileCount} archivos subidos exitosamente`;
      },
      error: {
        message: 'Error subiendo archivo',
        description: GENERIC_ERROR_MESSAGE,
      },
    });
  };
  const handleClose = () => {
    form.reset();
    reset();
    onHide?.();
  };

  return (
    <ControlledModal
      centered
      show={show}
      onHide={handleClose}
      title="Subir archivo"
      description={`Estás creando un nuevo archivo en ${activeFolder.metadata.name || 'la raíz'}.`}
      closeButton
    >
      <Modal.Body>
        <UploadFileForm form={form} onSubmit={handleUploadWithToast}>
          {({ isValidating }) => (
            <ControlledModal.Actions
              handleClose={handleClose}
              cancelButton
              isPending={isLoading || isValidating}
            />
          )}
        </UploadFileForm>
      </Modal.Body>
    </ControlledModal>
  );
}
